/**
 * Script List Page - Responsive table/cards aligning with the Contact Tel brand.
 */
import {
  useEffect,
  useState,
  useCallback,
  useMemo,
  type ChangeEvent,
} from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Card,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Skeleton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery,
  useTheme,
  Switch,
  Tooltip,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Add, Edit, Delete, Code, Clear, Refresh } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { useConfirmDialog } from '../components/ConfirmDialog';
import { RequirePermission } from '../contexts/PermissionContext';
import { useListSearchParams } from '../hooks/useListSearchParams';
import LinkPagination from '../components/LinkPagination';
import LinkTableCell from '../components/LinkTableCell';
import FilteredListLayout from '../components/FilteredListLayout';
import ListActionButton from '../components/ListActionButton';
import type { Script } from '../types';
import usePageTitle from '../hooks/usePageTitle';

const DEFAULT_ORDERING = 'name';

export default function ScriptListPage() {
  const { t, i18n } = useTranslation(['pages', 'common']);
  usePageTitle(t('pages:scripts.title'));
  const [scripts, setScripts] = useState<Script[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalScripts, setTotalScripts] = useState(0);
  const { confirm, DialogComponent } = useConfirmDialog();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isDarkMode = theme.palette.mode === 'dark';
  const filterInputSx = {
    borderRadius: '10px',
    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.04)' : '#ffffff',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
    },
    '& .MuiSelect-select': {
      padding: '11px 12px',
    },
    '& .MuiInputBase-input': {
      padding: '11px 12px',
    },
  };
  const columnTitle = (label?: string) => (label ?? '').toLocaleUpperCase(i18n.language);

  const {
    filters,
    page,
    rowsPerPage,
    ordering,
    setParams,
    setPage,
    setRowsPerPage,
    getPageLink,
  } = useListSearchParams({
    filters: ['status', 'event_type', 'search'],
    defaultPage: 1,
    defaultPageSize: 10,
    defaultOrdering: DEFAULT_ORDERING,
  });

  const statusFilterValue = filters.status ?? '';
  const eventTypeFilterValue = filters.event_type ?? '';
  const searchFilterValue = filters.search ?? '';
  const orderingParam = ordering || DEFAULT_ORDERING;

  const statusOptions = useMemo(
    () => [
      { value: 'active', label: t('pages:scripts.status.active') },
      { value: 'inactive', label: t('pages:scripts.status.inactive') },
    ],
    [t],
  );

  const eventTypeOptions = useMemo(() => {
    const translations = t('pages:scripts.eventTypes', { returnObjects: true }) as Record<string, string> | undefined;
    return Object.entries(translations ?? {}).map(([value, label]) => ({ value, label }));
  }, [t]);

  const orderingOptions = useMemo(
    () => [
      { value: 'name', label: t('pages:scripts.filters.orderingOptions.nameAsc') },
      { value: '-name', label: t('pages:scripts.filters.orderingOptions.nameDesc') },
    ],
    [t],
  );

  const fetchScripts = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number | boolean> = {
        page: page + 1,
        page_size: rowsPerPage,
        ordering: orderingParam,
      };

      if (statusFilterValue === 'active') {
        params.is_active = true;
      } else if (statusFilterValue === 'inactive') {
        params.is_active = false;
      }

      if (eventTypeFilterValue) {
        params.event_type = eventTypeFilterValue;
      }

      if (searchFilterValue) {
        params.search = searchFilterValue;
      }

      const response = await api.get('/api/scripts/', { params });
      const data = response.data.results ?? response.data;
      setScripts(Array.isArray(data) ? data : []);
      setTotalScripts(response.data.count ?? data.length ?? 0);
    } catch (error) {
      console.error('Error loading scripts:', error);
      setScripts([]);
      setTotalScripts(0);
    } finally {
      setLoading(false);
    }
  }, [orderingParam, page, rowsPerPage, statusFilterValue, eventTypeFilterValue, searchFilterValue]);

  useEffect(() => {
    fetchScripts();
  }, [fetchScripts]);

  const toggleActive = async (id: string, isActive: boolean) => {
    try {
      await api.patch(`/api/scripts/${id}/`, { isActive: !isActive });
      fetchScripts();
    } catch (error) {
      console.error('Error changing status:', error);
    }
  };

  const handleDelete = async (id: string) => {
    const confirmed = await confirm({
      title: t('pages:scripts.deleteScript.title'),
      message: t('pages:scripts.deleteScript.message'),
      type: 'error',
      icon: <Delete color="error" />,
      confirmIcon: <Delete />,
      confirmText: t('pages:scripts.deleteScript.confirm'),
    });

    if (!confirmed) return;

    try {
      await api.delete(`/api/scripts/${id}/`);
      fetchScripts();
    } catch (error) {
      console.error('Error deleting:', error);
    }
  };

  const hasFilters =
    Boolean(searchFilterValue) ||
    Boolean(statusFilterValue) ||
    Boolean(eventTypeFilterValue) ||
    orderingParam !== DEFAULT_ORDERING;

  const headerActions = (
    <>
      <ListActionButton
        startIcon={<Refresh />}
        onClick={fetchScripts}
        disabled={loading}
      >
        {t('common:actions.refresh')}
      </ListActionButton>
      <RequirePermission permission="scripts_create">
        <ListActionButton
          variant="contained"
          startIcon={<Add />}
          component={RouterLink}
          to="/scripts/new"
        >
          {t('pages:scripts.new')}
        </ListActionButton>
      </RequirePermission>
    </>
  );

  const filterControls = (
    <>
      <TextField
        size="small"
        label={t('common:actions.search')}
        placeholder={t('pages:scripts.filters.searchPlaceholder')}
        value={searchFilterValue}
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          const value = event.target.value;
          setParams({ search: value || undefined }, { resetPage: true });
        }}
        sx={{ ...filterInputSx, minWidth: 220, flex: '1 1 220px' }}
      />
      <FormControl size="small" sx={{ minWidth: 180, ...filterInputSx }}>
        <InputLabel id="scripts-status-filter-label">{t('pages:scripts.filters.statusLabel')}</InputLabel>
        <Select
          labelId="scripts-status-filter-label"
          value={statusFilterValue}
          label={t('pages:scripts.filters.statusLabel')}
          onChange={(event: SelectChangeEvent<string>) => {
            const value = event.target.value;
            setParams({ status: value || undefined }, { resetPage: true });
          }}
        >
          <MenuItem value="">
            <em>{t('common:common.all')}</em>
          </MenuItem>
          {statusOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
          <FormControl size="small" sx={{ minWidth: 180, ...filterInputSx }}>
            <InputLabel id="scripts-event-filter-label">{t('pages:scripts.filters.eventTypeLabel')}</InputLabel>
            <Select
              labelId="scripts-event-filter-label"
              value={eventTypeFilterValue}
              label={t('pages:scripts.filters.eventTypeLabel')}
              onChange={(event: SelectChangeEvent<string>) => {
            const value = event.target.value;
            setParams({ event_type: value || undefined }, { resetPage: true });
          }}
        >
          <MenuItem value="">
            <em>{t('common:common.all')}</em>
          </MenuItem>
          {eventTypeOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ minWidth: 200, ...filterInputSx }}>
        <InputLabel id="scripts-ordering-filter-label">{t('pages:scripts.filters.orderingLabel')}</InputLabel>
        <Select
          labelId="scripts-ordering-filter-label"
          value={orderingParam}
          label={t('pages:scripts.filters.orderingLabel')}
          onChange={(event: SelectChangeEvent<string>) => {
            setParams({ ordering: event.target.value }, { resetPage: true });
          }}
        >
          {orderingOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Button
        variant="outlined"
        startIcon={<Clear />}
        disabled={!hasFilters}
        onClick={() => {
          if (!hasFilters) return;
          setParams({ search: null, status: null, event_type: null, ordering: DEFAULT_ORDERING }, { resetPage: true });
        }}
        sx={{
          textTransform: 'none',
          color: theme.palette.primary.main,
          borderColor: alpha(theme.palette.primary.main, 0.6),
          '&:hover': {
            borderColor: theme.palette.primary.main,
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
          },
        }}
      >
        {t('common:actions.clear')}
      </Button>
    </>
  );

  const renderTable = () => {
    if (isMobile) {
      if (loading) {
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 3 }}>
            {[...Array(3)].map((_, index) => (
              <Card
                key={index}
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                  boxShadow:
                    theme.palette.mode === 'dark'
                      ? '0 25px 45px rgba(0,0,0,0.6)'
                      : '0 15px 35px rgba(0,0,0,0.08)',
                }}
              >
                <Box sx={{ p: 2 }}>
                  <Skeleton height={28} width="60%" />
                  <Skeleton height={18} sx={{ mt: 1, width: '40%' }} />
                  <Skeleton height={32} sx={{ mt: 2 }} />
                </Box>
              </Card>
            ))}
          </Box>
        );
      }

      if (scripts.length === 0) {
        return (
          <Card
            sx={{
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
              boxShadow: 'none',
            }}
          >
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Code sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                {t('pages:scripts.noScripts')}
              </Typography>
              <RequirePermission permission="scripts_create">
                <ListActionButton
                  startIcon={<Add />}
                  component={RouterLink}
                  to="/scripts/new"
                >
                  {t('pages:scripts.new')}
                </ListActionButton>
              </RequirePermission>
            </Box>
          </Card>
        );
      }

      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 3 }}>
          {scripts.map((script) => (
            <Card
              key={script.id}
              sx={{
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                boxShadow:
                  theme.palette.mode === 'dark'
                    ? '0 25px 45px rgba(0,0,0,0.6)'
                    : '0 15px 35px rgba(0,0,0,0.08)',
              }}
            >
              <Box sx={{ px: 2, pt: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ color: theme.palette.primary.main }}>
                  {script.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {script.description}
                </Typography>
              </Box>
              <Box sx={{ px: 2, py: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  label={t(`pages:scripts.status.${script.isActive ? 'active' : 'inactive'}`)}
                  size="small"
                  variant="outlined"
                  sx={{
                    color: script.isActive ? theme.palette.success.main : theme.palette.text.secondary,
                    bgcolor: script.isActive ? alpha(theme.palette.success.main, 0.2) : alpha(theme.palette.text.secondary, 0.12),
                  }}
                />
                <Chip
                  label={t(`pages:scripts.scriptTypes.${script.scriptType || 'code'}`)}
                  size="small"
                  variant="outlined"
                />
                <Chip label={t(`pages:scripts.eventTypes.${script.eventType}`, script.eventType)} size="small" variant="outlined" />
              </Box>
              <Box
                sx={{
                  px: 2,
                  py: 2,
                  borderTop: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Switch
                  checked={script.isActive}
                  onChange={() => toggleActive(script.id, script.isActive)}
                  color="primary"
                />
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Button size="small" variant="outlined" component={RouterLink} to={`/scripts/${script.id}`}>
                    {t('common:actions.edit')}
                  </Button>
                  <Button size="small" variant="outlined" color="error" onClick={() => handleDelete(script.id)}>
                    {t('common:actions.delete')}
                  </Button>
                </Box>
              </Box>
            </Card>
          ))}
        </Box>
      );
    }

    const columnsCount = 5;

    return (
      <Table>
        <TableHead>
          <TableRow sx={{
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            '& th': {
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              fontSize: '0.75rem',
            },
          }}>
            <TableCell>{columnTitle(t('pages:scripts.name'))}</TableCell>
            <TableCell>{columnTitle(t('pages:scripts.table.type'))}</TableCell>
            <TableCell>{columnTitle(t('pages:scripts.table.status'))}</TableCell>
            <TableCell>{columnTitle(t('pages:scripts.filters.eventTypeLabel'))}</TableCell>
            <TableCell align="center">{columnTitle(t('common:actions.actions'))}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            [...Array(5)].map((_, i) => (
              <TableRow key={i}>
                {[...Array(columnsCount)].map((_, j) => (
                  <TableCell key={j}>
                    <Skeleton />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : scripts.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columnsCount} align="center" sx={{ py: 8 }}>
                <Code sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                  {t('pages:scripts.noScripts')}
                </Typography>
                <RequirePermission permission="scripts_create">
                  <ListActionButton
                    startIcon={<Add />}
                    component={RouterLink}
                    to="/scripts/new"
                  >
                    {t('pages:scripts.new')}
                  </ListActionButton>
                </RequirePermission>
              </TableCell>
            </TableRow>
          ) : (
            scripts.map((script) => (
              <TableRow key={script.id} hover>
                <LinkTableCell to={`/scripts/${script.id}`}>
                  <Box>
                    <Typography
                      fontWeight={600}
                      sx={{
                        '&:hover': { color: 'primary.main', textDecoration: 'underline' },
                      }}
                    >
                      {script.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {script.description}
                    </Typography>
                  </Box>
                </LinkTableCell>
                <TableCell>
                  <Chip
                    label={t(`pages:scripts.scriptTypes.${script.scriptType || 'code'}`)}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={t(`pages:scripts.status.${script.isActive ? 'active' : 'inactive'}`)}
                    size="small"
                    variant="outlined"
                    sx={{
                      color: script.isActive ? theme.palette.success.main : theme.palette.text.secondary,
                      bgcolor: script.isActive ? alpha(theme.palette.success.main, 0.2) : alpha(theme.palette.text.secondary, 0.12),
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Chip label={t(`pages:scripts.eventTypes.${script.eventType}`, script.eventType)} size="small" variant="outlined" />
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                    <Tooltip title={t('common:actions.edit')}>
                      <IconButton
                        size="small"
                        component={RouterLink}
                        to={`/scripts/${script.id}`}
                        aria-label={t('common:actions.edit')}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Button size="small" variant="text" onClick={() => toggleActive(script.id, script.isActive)}>
                      <Tooltip title={t('pages:scripts.toggle')} arrow>
                        <Switch checked={script.isActive} color="primary" />
                      </Tooltip>
                    </Button>
                    <Tooltip title={t('common:actions.delete')}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(script.id)}
                        aria-label={t('common:actions.delete')}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    );
  };

  const tableContent = renderTable();

  const pagination = (
    <LinkPagination
      component="div"
      count={totalScripts}
      page={page}
      onPageChange={(_, newPage) => setPage(newPage)}
      rowsPerPage={rowsPerPage}
      onRowsPerPageChange={(event) => setRowsPerPage(Number(event.target.value))}
      rowsPerPageOptions={[10, 25, 50]}
      labelRowsPerPage={t('common:pagination.rowsPerPage')}
      getItemAriaLabel={(type) => {
        if (type === 'first') return t('common:pagination.firstPage');
        if (type === 'last') return t('common:pagination.lastPage');
        if (type === 'next') return t('common:pagination.nextPage');
        return t('common:pagination.previousPage');
      }}
      getPageLink={getPageLink}
    />
  );

  const tableFooter = !loading && scripts.length > 0 ? pagination : undefined;

  return (
    <Box>
      <FilteredListLayout
        headerProps={{
          icon: <Code sx={{ fontSize: 32, color: 'primary.main' }} />,
          title: t('pages:scripts.title'),
          subtitle: t('pages:scripts.subtitle'),
          actions: headerActions,
        }}
        filterControls={filterControls}
        table={tableContent}
        tableFooter={tableFooter}
      />

      {DialogComponent}
    </Box>
  );
}
